<template>
  <div>
    <v-container>
      <div class="generator-content-well">
        <div class="mode-toggle mb-6">
          <label class="flex items-center mr-6">
            <input type="radio" v-model="mode" value="single" class="mr-2" />
            Single Submission
          </label>
          <label class="flex items-center">
            <input type="radio" v-model="mode" value="csv" class="mr-2" />
            CSV Bulk Upload
          </label>
        </div>

        <div v-if="mode === 'single'">
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="pa-6 mb-4">
                <h5 class="font-semibold mb-4 text-lg">Product Configuration</h5>

                <Dropdown
                  :value="form.productType"
                  label="Product Type"
                  :required="true"
                  :options="productTypeOptions"
                  class="mb-4"
                  @change="onProductTypeChange" />

                <Dropdown
                  :value="form.state"
                  label="State"
                  :required="true"
                  :options="stateOptions"
                  class="mb-4"
                  @change="val => form.state = val" />
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card class="pa-6 mb-4">
                <h5 class="font-semibold mb-4 text-lg">Insured Information</h5>

                <p class="text-sm grey--text mb-4">Address fields are optional — leave blank to auto-generate.</p>

                <TextInput
                  :value="form.address.streetNumber"
                  label="Street Number (Optional)"
                  class="mb-4"
                  @change="val => form.address.streetNumber = val" />

                <TextInput
                  :value="form.address.street"
                  label="Street (Optional)"
                  class="mb-4"
                  @change="val => form.address.street = val" />

                <TextInput
                  :value="form.address.city"
                  label="City (Optional)"
                  class="mb-4"
                  @change="val => form.address.city = val" />

                <TextInput
                  :value="form.address.zip"
                  label="ZIP Code (Optional)"
                  class="mb-4"
                  @change="val => form.address.zip = val" />

                <TextInput
                  :value="form.firstName"
                  label="First Name (Optional)"
                  class="mb-4"
                  @change="val => form.firstName = val" />

                <TextInput
                  :value="form.lastName"
                  label="Last Name (Optional)"
                  class="mb-4"
                  @change="val => form.lastName = val" />

                <label class="flex items-center mb-4" style="cursor:pointer">
                  <input type="checkbox" v-model="form.mailing.enabled" class="mr-2" />
                  Different mailing address
                </label>

                <template v-if="form.mailing.enabled">
                  <TextInput
                    :value="form.mailing.street"
                    label="Mailing Street Address"
                    class="mb-4"
                    @change="val => form.mailing.street = val" />

                  <TextInput
                    :value="form.mailing.city"
                    label="Mailing City"
                    class="mb-4"
                    @change="val => form.mailing.city = val" />

                  <Dropdown
                    :value="form.mailing.state"
                    label="Mailing State"
                    :options="stateOptions"
                    class="mb-4"
                    @change="val => form.mailing.state = val" />

                  <TextInput
                    :value="form.mailing.zip"
                    label="Mailing ZIP Code"
                    class="mb-4"
                    @change="val => form.mailing.zip = val" />
                </template>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="d-flex justify-end">
              <FormButton
                label="Clear Form"
                color="secondary"
                :text="true"
                class="mr-4"
                :disabled="isCreating"
                @click="resetForm" />

              <v-tooltip top :disabled="isFormValid" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <FormButton
                      :label="isCreating ? 'Creating...' : 'Create Submission'"
                      color="secondary"
                      :disabled="!isFormValid || isCreating"
                      @click="createSubmission" />
                  </span>
                </template>
                <span>{{ validationMessage }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </div>

        <!-- CSV Bulk Upload Section -->
        <div v-if="mode === 'csv'">
          <v-card class="pa-6 mb-6">
            <h5 class="font-semibold mb-2 text-lg">Upload CSV</h5>
            <p class="text-sm grey--text mb-4">
              Required columns: <code>state</code>, <code>productType</code><br />
              Optional columns: <code>firstName</code>, <code>lastName</code>,
              <code>address</code>, <code>city</code>, <code>zip</code>,
              <code>mailingAddress</code>, <code>mailingCity</code>, <code>mailingState</code>, <code>mailingZip</code>
              — address fields auto-generate if omitted; mailing address omitted means same as risk.
            </p>
            <div
              class="csv-dropzone"
              @dragover.prevent
              @drop.prevent="onCsvDrop">
              <input
                type="file"
                accept=".csv"
                ref="csvFileInput"
                class="csv-file-input"
                @change="onCsvFileChange" />
              <div class="csv-dropzone-label">
                <span v-if="!csvFile">Drop a CSV file here or <span class="primary--text cursor-pointer" @click="$refs.csvFileInput.click()">browse</span></span>
                <span v-else>{{ csvFile.name }} — {{ csvRows.length }} row(s) parsed</span>
              </div>
            </div>
            <p v-if="csvParseError" class="red--text text-sm mt-2">{{ csvParseError }}</p>
          </v-card>

          <div v-if="csvRows.length > 0" class="mb-6">
            <h5 class="font-semibold mb-4 text-lg">Preview ({{ csvRows.length }} rows)</h5>
            <div class="results-table">
              <table class="w-full">
                <thead>
                  <tr class="table-header">
                    <th class="p-3 text-left">Name</th>
                    <th class="p-3 text-left">Address</th>
                    <th class="p-3 text-left">State</th>
                    <th class="p-3 text-left">Product Type</th>
                    <th class="p-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in csvRows"
                      :key="index"
                      :class="{ 'row-alt': index % 2 === 0 }">
                    <td class="p-3">{{ row.name }}</td>
                    <td class="p-3">{{ row.address ? `${row.address}, ${row.city}, ${row.state} ${row.zip}` : '(auto-generate)' }}</td>
                    <td class="p-3">{{ row.state }}</td>
                    <td class="p-3">{{ row.productType }}</td>
                    <td class="p-3">
                      <span v-if="row._status === 'success'" class="green--text">✓ Created</span>
                      <span v-else-if="row._status === 'error'" class="red--text" :title="row._error">✗ Failed</span>
                      <span v-else-if="row._status === 'pending'" class="grey--text">Creating...</span>
                      <span v-else class="grey--text">Queued</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <v-row class="mt-4">
              <v-col class="d-flex justify-end">
                <FormButton
                  label="Clear"
                  color="secondary"
                  :text="true"
                  class="mr-4"
                  :disabled="isCreatingBulk"
                  @click="clearCsv" />
                <FormButton
                  :label="isCreatingBulk ? `Creating... (${csvProgress}/${csvRows.length})` : `Create All (${csvRows.length})`"
                  color="secondary"
                  :disabled="isCreatingBulk || csvRows.length === 0"
                  @click="createAllFromCsv" />
              </v-col>
            </v-row>
          </div>
        </div>

        <div v-if="createdSubmissions.length > 0" class="results-section mt-8">
          <h5 class="font-semibold mb-4 text-lg">Created Submissions</h5>
          <div class="results-table">
            <table class="w-full">
              <thead>
                <tr class="table-header">
                  <th class="p-3 text-left">Submission ID</th>
                  <th class="p-3 text-left">Insured Name</th>
                  <th class="p-3 text-left">State</th>
                  <th class="p-3 text-left">Product Type</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(submission, index) in createdSubmissions"
                    :key="submission.submissionId"
                    :class="{ 'row-alt': index % 2 === 0 }">
                  <td class="p-3">
                    <a :href="getSubmissionUrl(submission.submissionId)"
                       target="_blank"
                       class="primary--text">
                      {{ submission.submissionId }}
                    </a>
                  </td>
                  <td class="p-3">{{ submission.accountName }}</td>
                  <td class="p-3">{{ submission.state }}</td>
                  <td class="p-3">{{ submission.productType }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import FormButton from '@/components/form/FormButton.vue';
import TextInput from '@/components/form/TextInput.vue';
import Dropdown from '@/components/form/Dropdown.vue';
import { FormStateService } from '@/api/FormStateService';
import { SubmissionService } from '@/api/SubmissionService';
import { GateService } from '@/api/GateService';
import { UserProfileService } from '@/api/UserProfileService';
import { UnderwriterService } from '@/api/UnderwriterService';
import Environment from '@/scripts/environment';

// Submission templates
import plSubmissionBaseTemplate from '@/data/submissionTemplates/pl-submission-base.json';
import hoFormSteps from '@/data/submissionTemplates/pl-ho-form-steps.json';
import floodFormSteps from '@/data/submissionTemplates/pl-flood-form-steps.json';
import { fakeAddressPool } from '@/data/fakeAddressPool';

// Address pool sourced from shared/tools/IQ.Tools/Repositories/FakeDataRepository.cs
const homeownersAddresses = fakeAddressPool.find(s => s.ProductType === 'homeowners')?.Addresses ?? [];
const floodAddresses = fakeAddressPool.find(s => s.ProductType === 'flood')?.Addresses ?? [];
const fallbackAddresses = fakeAddressPool.find(s => s.ProductType === 'fallback')?.Addresses ?? [];

const PL_PRODUCT_MAP = {
  'homeowners': { coverageOption: 'ho3', formStateId: 'HO' },
  'condo': { coverageOption: 'ho6', formStateId: 'HO' },
  'renters': { coverageOption: 'ho4', formStateId: 'HO' },
  'dwelling': { coverageOption: 'dp3', formStateId: 'HO' },
  'wind-home': { coverageOption: 'ho3_windonly', formStateId: 'HO' },
  'wind-condo': { coverageOption: 'ho6_windonly', formStateId: 'HO' },
  'flood': { coverageOption: 'flood', formStateId: 'Flood' }
};

const PL_FORM_TYPE_MAP = {
  'homeowners': { Value: 'HO3', Id: '4d2fed87-8610-4367-8f96-3b9cabc316ee' },
  'condo': { Value: 'HO6', Id: '999f1316-af34-4e67-a51c-3b155f0f386e' },
  'renters': { Value: 'HO4', Id: 'e3d1d295-976d-44c0-a96b-daabac8f5cc3' },
  'dwelling': { Value: 'DP3', Id: 'c9cad138-aa82-43b4-b443-4c41d91c7420' },
  'wind-home': { Value: 'HO3', Id: '4d2fed87-8610-4367-8f96-3b9cabc316ee' },
  'wind-condo': { Value: 'HO6', Id: '999f1316-af34-4e67-a51c-3b155f0f386e' },
  'flood': { Value: 'Primary Flood', Id: 'c9b03f4e-8a4f-4a86-a439-8c03f6a73116' }
};

// Hardcoded default retail agent for non-flood submissions
const DEFAULT_PL_AGENT = {
  AgencyKey: '/28562/4230614/4230614',
  SeekDisplay: 'Accounting Contact (abcconly@libertycompany.com) - The Liberty Company Insurance Brokers, Inc. - Ventura',
  key: '/28562/4230614/595950',
  Producer: false,
  Licenses: [],
  LicenseTags: '',
  AmlinkEnabled: true,
  Active: true,
  Name: 'Accounting Contact',
  Salutation: null,
  MiddleName: null,
  Suffix: null,
  OrgName: 'The Liberty Company Insurance Brokers, Inc.',
  BranchName: 'Ventura',
  Title: '',
  Street1: '21 S California St',
  Street2: 'Suite 308',
  City: 'Ventura',
  State: 'CA',
  Postal: '93001',
  County: null,
  Country: null,
  GeoLatitude: null,
  GeoLongitude: null,
  Phone: '805.702.1257',
  Mobile: null,
  Updated: '2026-01-20T18:23:21.673+00:00',
  Email: 'abcconly@libertycompany.com',
  FirstName: 'Accounting',
  LastName: 'Contact',
  HumanId: 595950,
  OrgBranchId: 4230614,
  OrgId: 28562,
  BranchId: 4224560,
  Role: null,
  id: '595950'
};

const DEFAULT_PL_AGENCY = {
  key: '/28562/4230614/4230614',
  SeekDisplay: 'The Liberty Company Insurance Brokers, Inc. - Ventura',
  HasNonProdAccess: false,
  IsOrgPartition: false,
  Active: true,
  Name: null,
  Salutation: null,
  MiddleName: null,
  Suffix: null,
  OrgName: 'The Liberty Company Insurance Brokers, Inc.',
  BranchName: 'Ventura',
  Title: null,
  Street1: '21 S California St',
  Street2: 'Suite 308',
  City: 'Ventura',
  State: 'CA',
  Postal: '93001',
  County: null,
  Country: null,
  GeoLatitude: null,
  GeoLongitude: null,
  Phone: null,
  Mobile: null,
  Updated: '2026-01-20T18:23:00+00:00',
  Email: null,
  FirstName: null,
  LastName: null,
  HumanId: null,
  OrgBranchId: 4230614,
  OrgId: 28562,
  BranchId: 4224560,
  Role: null,
  id: '4230614'
};

// Hardcoded IQ Flood underwriter
const IQ_FLOOD_UNDERWRITER = {
  SeekDisplay: 'IQ Flood (floodreferrals.access@amwins.com)',
  AutocompleteDisplay: 'IQ Flood - Charlotte, NC',
  key: '460997',
  NotificationEmail: null,
  Active: true,
  Name: 'IQ Flood',
  Salutation: null,
  MiddleName: null,
  Suffix: null,
  OrgName: 'Access',
  BranchName: 'Amwins IQ',
  Title: '',
  Street1: '4725 Piedmont Row Drive',
  Street2: 'Suite 600',
  City: 'Charlotte',
  State: 'NC',
  Postal: '28210',
  County: null,
  Country: null,
  GeoLatitude: null,
  GeoLongitude: null,
  Phone: '704.749.2700',
  Mobile: null,
  Updated: '2025-03-10T09:46:02.133+00:00',
  Email: 'floodreferrals.access@amwins.com',
  FirstName: 'IQ',
  LastName: 'Flood',
  HumanId: 460997,
  OrgBranchId: 3467683,
  OrgId: 36866,
  BranchId: 3462469,
  Role: null,
  id: '460997'
};

const US_STATES = [
  { text: 'Alabama', value: 'AL' },
  { text: 'Alaska', value: 'AK' },
  { text: 'Arizona', value: 'AZ' },
  { text: 'Arkansas', value: 'AR' },
  { text: 'California', value: 'CA' },
  { text: 'Colorado', value: 'CO' },
  { text: 'Connecticut', value: 'CT' },
  { text: 'Delaware', value: 'DE' },
  { text: 'Florida', value: 'FL' },
  { text: 'Georgia', value: 'GA' },
  { text: 'Hawaii', value: 'HI' },
  { text: 'Idaho', value: 'ID' },
  { text: 'Illinois', value: 'IL' },
  { text: 'Indiana', value: 'IN' },
  { text: 'Iowa', value: 'IA' },
  { text: 'Kansas', value: 'KS' },
  { text: 'Kentucky', value: 'KY' },
  { text: 'Louisiana', value: 'LA' },
  { text: 'Maine', value: 'ME' },
  { text: 'Maryland', value: 'MD' },
  { text: 'Massachusetts', value: 'MA' },
  { text: 'Michigan', value: 'MI' },
  { text: 'Minnesota', value: 'MN' },
  { text: 'Mississippi', value: 'MS' },
  { text: 'Missouri', value: 'MO' },
  { text: 'Montana', value: 'MT' },
  { text: 'Nebraska', value: 'NE' },
  { text: 'Nevada', value: 'NV' },
  { text: 'New Hampshire', value: 'NH' },
  { text: 'New Jersey', value: 'NJ' },
  { text: 'New Mexico', value: 'NM' },
  { text: 'New York', value: 'NY' },
  { text: 'North Carolina', value: 'NC' },
  { text: 'North Dakota', value: 'ND' },
  { text: 'Ohio', value: 'OH' },
  { text: 'Oklahoma', value: 'OK' },
  { text: 'Oregon', value: 'OR' },
  { text: 'Pennsylvania', value: 'PA' },
  { text: 'Rhode Island', value: 'RI' },
  { text: 'South Carolina', value: 'SC' },
  { text: 'South Dakota', value: 'SD' },
  { text: 'Tennessee', value: 'TN' },
  { text: 'Texas', value: 'TX' },
  { text: 'Utah', value: 'UT' },
  { text: 'Vermont', value: 'VT' },
  { text: 'Virginia', value: 'VA' },
  { text: 'Washington', value: 'WA' },
  { text: 'West Virginia', value: 'WV' },
  { text: 'Wisconsin', value: 'WI' },
  { text: 'Wyoming', value: 'WY' }
];

export default {
  name: 'AdminSubmissionGenerator',
  components: {
    FormButton,
    TextInput,
    Dropdown
  },
  data () {
    return {
      mode: 'single',
      form: {
        productType: '',
        state: '',
        address: {
          streetNumber: '',
          street: '',
          city: '',
          zip: ''
        },
        firstName: '',
        lastName: '',
        mailing: {
          enabled: false,
          street: '',
          city: '',
          state: '',
          zip: ''
        }
      },
      productTypeOptions: [
        { text: 'Condo', value: 'condo' },
        { text: 'Dwelling', value: 'dwelling' },
        { text: 'Flood', value: 'flood' },
        { text: 'Homeowners', value: 'homeowners' },
        { text: 'Renters', value: 'renters' },
        { text: 'Wind - Condo', value: 'wind-condo' },
        { text: 'Wind - Home', value: 'wind-home' }
      ],
      stateOptions: US_STATES,
      isCreating: false,
      createdSubmissions: [],
      csvFile: null,
      csvRows: [],
      csvParseError: '',
      isCreatingBulk: false,
      csvProgress: 0
    };
  },
  mounted () {
    // Block prod and non-admin access
    if (Environment.environment === 'prod') {
      this.$store.dispatch('toastError', { Message: 'This feature is not available in the production environment.' });
      this.$router.push({ name: 'Dashboard' });
      return;
    }
    if (!this.$store.getters.userHasRole('admin')) {
      this.$router.push({ path: '/unauthorized' });
    }
  },
  computed: {
    isFormValid () {
      return !!(this.form.productType && this.form.state);
    },
    validationMessage () {
      const missing = [];
      if (!this.form.productType) missing.push('Product Type');
      if (!this.form.state) missing.push('State');
      return missing.length > 0 ? `Please fill in: ${missing.join(', ')}` : '';
    }
  },
  methods: {
    onProductTypeChange (value) {
      this.form.productType = value;
    },

    // Same state by productType, use fallback addresses if no state matches productType
    generateAddress (productType, state) {
      const primaryPool = productType === 'flood'
        ? floodAddresses
        : homeownersAddresses;

      const primaryMatches = primaryPool.filter(a => a.State === state);
      const fallbackMatches = fallbackAddresses.filter(a => a.State === state);
      const candidates = primaryMatches.length > 0 ? primaryMatches : fallbackMatches;

      if (candidates.length === 0) {
        throw new Error(`No addresses found for state: ${state}`);
      }

      const raw = candidates[Math.floor(Math.random() * candidates.length)];
      const parts = raw.Address.split(',').map(p => p.trim());
      const streetParts = parts[0].split(' ');
      const streetNumber = streetParts[0];
      const street = streetParts.slice(1).join(' ');
      const city = parts[1];
      const stateZipParts = parts[2].split(' ');
      const zip = stateZipParts[stateZipParts.length - 1];
      const county = parts[3] ? parts[3].replace(/ County$/, '').trim() : null;

      return {
        streetNumber,
        street,
        city,
        state,
        zip,
        county,
        fullAddress: `${streetNumber} ${street}, ${city}, ${state} ${zip}`
      };
    },

    async createPlSubmission ({ productType, name, state, address, mailingAddress }) {
      const plProduct = PL_PRODUCT_MAP[productType];
      const formType = PL_FORM_TYPE_MAP[productType];

      if (!plProduct) {
        throw new Error(`Unknown personal lines product type: ${productType}`);
      }

      const isFlood = productType === 'flood';
      const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      const now = new Date();
      const effectiveDate = now.toISOString().split('T')[0] + 'T00:00:00';
      const expirationDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate())
        .toISOString().split('T')[0] + 'T00:00:00';

      // Fetch user profile and amlink in parallel
      const [userProfile, amlink] = await Promise.all([
        UserProfileService.get(),
        UserProfileService.amlink()
      ]);

      const underwriter = isFlood
        ? IQ_FLOOD_UNDERWRITER
        : await UnderwriterService.get({ id: userProfile.DefaultUnderwriterKey });

      const agent = DEFAULT_PL_AGENT;
      const agency = DEFAULT_PL_AGENCY;

      // Create FormState to get submission ID
      const formStatePayload = {
        FormState: {
          id: plProduct.formStateId,
          TemplateType: plProduct.formStateId,
          steps: isFlood ? floodFormSteps : hoFormSteps
        },
        FormStateRequest: {
          resultData: {
            CoverageOption: { value: plProduct.coverageOption },
            Underwriter: { value: underwriter },
            Agent: { value: agent },
            Agency: { value: agency },
            CreateDate: { value: today },
            OCRFile: null
          },
          data: {
            ocrFile: null,
            cloneAccountId: null
          }
        }
      };

      const formStateResult = await FormStateService.post({ body: formStatePayload });
      const submissionId = formStateResult.ResultId;
      const formStateId = formStateResult.id;

      // Parse name into first/last
      const nameParts = name.trim().split(' ');
      const firstName = nameParts[0] || 'Admin';
      const lastName = nameParts.slice(1).join(' ') || 'Generated';

      // Compute sid: the numeric suffix of ResultId, negated
      const sid = -parseInt(submissionId.replace(/^[A-Za-z-]+/, ''), 10);

      const creator = {
        Email: amlink.HumanEmail || '',
        FirstName: amlink.HumanFirstName,
        LastName: amlink.HumanLastName,
        HumanId: amlink.HumanId,
        OrgBranchId: amlink.OrgBranchId,
        OrgId: amlink.OrgId,
        BranchId: null,
        Role: 'admin',
        id: null
      };

      const covA = 400000;
      const covB = 40000;
      const covC = 160000;
      const covD = 60000;

      const saveData = JSON.parse(JSON.stringify(plSubmissionBaseTemplate));

      // Inject dynamic fields
      saveData.CoverageOption.value = plProduct.coverageOption;
      saveData.CreateDate.value = now.toISOString().split('T')[0] + 'T00:00:00';
      saveData.Risk.value = {
        RiskKey: null,
        StreetAddress: null,
        StreetNumber: address.streetNumber,
        StreetName: address.street,
        UnitType: null,
        UnitNumber: null,
        City: address.city,
        State: address.state,
        PostalCode: address.zip,
        County: address.county,
        CountryCode: 'US',
        Formatted: `${address.streetNumber} ${address.street}, ${address.city}, ${address.state} ${address.zip} US`,
        Locked: true
      };
      saveData.EffectiveDate.value = effectiveDate;
      saveData.ExpirationDate.value = expirationDate;
      saveData.Insured.value.PrimaryInsured.FirstName = firstName;
      saveData.Insured.value.PrimaryInsured.LastName = lastName;
      saveData.FirstName.value = firstName;
      saveData.LastName.value = lastName;
      saveData.Underwriter.value = underwriter;
      saveData.Agency.value = agency;
      saveData.Agent.value = agent;
      saveData.CalculatedTIV = covA + covB + covC + covD;

      // Add story for Condo types; adjust building type and sub-type for carrier quoting
      if (productType === 'condo' || productType === 'wind-condo') {
        saveData.UnitStory = { value: 2 };
        saveData.BuildingType.value = 'Commercial - Condo/Apartment';
        saveData.BuildingTypeCondo = { value: 'Low Rise' };
      }

      // Simulate CovA.DataServiceValue to provide RCV for non-flood so carriers see a value.
      if (!isFlood) {
        saveData.CovA.DataServiceValue = saveData.CovA.value;
      }

      // Provide a mock distance-to-coast for coastal states
      // 5 miles is a safe stand-in (puts them in the > 1 mile bucket for all coastal state rules) given address data pool
      const COASTAL_STATES = ['FL', 'TX', 'LA', 'MS', 'AL', 'GA', 'SC', 'NC'];
      if (!isFlood && COASTAL_STATES.includes(address.state)) {
        saveData.WaterDistance = { value: 5 };
      }

      // Provide building/contents valuations and stories for flood
      if (isFlood) {
        saveData.BuildingValuationAmount = { value: saveData.CovA.value };
        saveData.ContentsValuationAmount = { value: saveData.CovC.value };
        saveData.NumStories = { value: 1 };
      }

      if (mailingAddress) {
        saveData.MailingAddress = {
          value: {
            RiskKey: null,
            StreetAddress: mailingAddress.street,
            StreetNumber: null,
            StreetName: null,
            UnitType: null,
            UnitNumber: null,
            City: mailingAddress.city,
            State: mailingAddress.state,
            PostalCode: mailingAddress.zip,
            CountryCode: 'US',
            Locked: true,
            Formatted: `${mailingAddress.street}, ${mailingAddress.city}, ${mailingAddress.state} ${mailingAddress.zip} US`
          }
        };
      }

      const formStateKey = plProduct.formStateId === 'Flood' ? 'Flood' : 'HO';

      const savePayload = {
        Version: isFlood ? 'V1' : 'V0',
        IsFloodSubmission: isFlood,
        VersionNumber: null,
        FormType: formType,
        RequestOrigin: 'Portal',
        CapiSubmissionId: null,
        IsDistributionRequest: false,
        DistributionDetails: null,
        IsIndication: false,
        RequestAgent: null,
        RenewalInfo: null,
        IsEditingRenewalApplication: false,
        AccountId: submissionId,
        id: submissionId,
        IdPrefix: 'SUB',
        sid,
        iid: 0,
        Creator: creator,
        UpdatedBy: null,
        Submitter: null,
        CreatedAt: now.toISOString(),
        UpdatedAt: '0001-01-01T00:00:00+00:00',
        SubmittedAt: null,
        Requests: [],
        Rejects: [],
        Complete: false,
        Renewal: false,
        SubmittedChannel: null,
        Data: saveData,
        DataServicePromises: {},
        DataService: { Google: null, Bams: null, MapRisk: null, KatRisk: null, CoreLogicGetValuationReport: null, CoreLogicSubmitValuation: null, CoreLogicSubmitValuation_InitialValuation: null, CoreLogicSubmitValuation_RefinementCall: null, CoreLogicRiskMeter: null, TigerCQAIR: null, TigerCQRMS: null, Verikai: null, CatAal: null, ClarionDoor: null, GlukWildfire: null, MunichRE: { MunichRECompactApiHistory: [] } },
        FormState: { [formStateKey]: formStateId },
        FormStatus: 'Active',
        CurrentForm: formStateKey,
        InitialForm: formStateKey,
        SubmittedToTfia: null,
        UnderwriterReferralDate: null,
        IssuedPolicyId: null,
        SelectedQuoteId: null,
        Quotes: [],
        HumanIdTags: `${amlink.HumanId},${agent.HumanId}`,
        HumanIds: [amlink.HumanId, agent.HumanId],
        OrgBranchId: agency.OrgBranchId,
        OrgId: agency.OrgId,
        key: `/${agency.OrgId}/${agency.OrgBranchId}/${submissionId}`
      };

      // Save and gate the submission
      let savedId = submissionId;
      if (isFlood) {
        const savedDoc = await SubmissionService.saveV1({ body: savePayload });
        savedId = savedDoc.id || savedDoc.AccountId || submissionId;
        await SubmissionService.savePreGateV1({ body: savedDoc });
        await GateService.gateTheSubmission({ body: savedDoc });
      } else {
        const savedDoc = await SubmissionService.save({ body: savePayload });
        savedId = savedDoc.id || savedDoc.AccountId || submissionId;
        await GateService.gateTheSubmission({ body: savedDoc });
      }

      // The FormState created by the POST may have a null data field or an un-hyphenated
      // ResultId (UAT backend normalizes IDs on save). QuoteForm.vue's mounted() guard
      // `if (formInit.data && formInit.ResultId)` must pass for the comparison grid to render,
      // so patch the FormState to guarantee both are set correctly.
      await FormStateService.patch({
        eventType: 'Save',
        body: { ...formStateResult, ResultId: savedId }
      });

      this.createdSubmissions.unshift({
        submissionId: savedId,
        accountName: name || '(No name)',
        state,
        productType
      });

      return savedId;
    },

    async createSubmission () {
      this.isCreating = true;

      try {
        const hasManualAddress = !!(
          this.form.address.streetNumber &&
          this.form.address.street &&
          this.form.address.city &&
          this.form.address.zip
        );

        let address;
        if (hasManualAddress) {
          address = {
            ...this.form.address,
            state: this.form.state,
            county: '',
            fullAddress: `${this.form.address.streetNumber} ${this.form.address.street}, ${this.form.address.city}, ${this.form.state} ${this.form.address.zip}`
          };
        } else {
          address = this.generateAddress(this.form.productType, this.form.state);
        }

        const firstName = this.form.firstName || 'Admin';
        const lastName = this.form.lastName || 'Generated';
        const name = `${firstName} ${lastName}`.trim();

        const hasMailing = this.form.mailing.enabled &&
          !!(this.form.mailing.street && this.form.mailing.city && this.form.mailing.state && this.form.mailing.zip);

        const submissionId = await this.createPlSubmission({
          productType: this.form.productType,
          name,
          state: this.form.state,
          address,
          mailingAddress: hasMailing ? this.form.mailing : null
        });

        this.$store.dispatch('toastSuccess', { Message: `Submission ${submissionId} created successfully!` });
      } catch (error) {
        console.error('Error creating submission:', error);
        this.$store.dispatch('toastError', { Message: error.message || 'Failed to create submission' });
      } finally {
        this.isCreating = false;
      }
    },

    // CSV methods
    parseCsvText (text) {
      const lines = text.trim().split(/\r?\n/);
      if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row.');

      const dataLines = lines.slice(1);
      if (dataLines.length > 25) throw new Error(`CSV exceeds the 25-row limit (found ${dataLines.length} rows). Please split into smaller batches.`);

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

      return dataLines.map((line, i) => {
        const values = this.parseCsvLine(line);
        const obj = {};
        headers.forEach((h, idx) => { obj[h] = values[idx] || ''; });

        if (!obj['state'] || !obj['producttype']) {
          throw new Error(`Row ${i + 2} is missing required columns (state, productType).`);
        }

        let productType = obj['producttype'];
        if (productType === 'home') productType = 'homeowners';

        const firstName = (obj['firstname'] || '').trim();
        const lastName = (obj['lastname'] || '').trim();
        const name = `${firstName} ${lastName}`.trim();

        return {
          name,
          address: obj['address'] || '',
          city: obj['city'] || '',
          state: obj['state'].toUpperCase(),
          zip: obj['zip'] || '',
          productType,
          mailingAddress: obj['mailingaddress'] || '',
          mailingCity: obj['mailingcity'] || '',
          mailingState: obj['mailingstate'] || '',
          mailingZip: obj['mailingzip'] || ''
        };
      });
    },

    // Handle commas in address line
    parseCsvLine (line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    },

    onCsvFileChange (event) {
      const input = event.target;
      if (input.files?.[0]) this.loadCsvFile(input.files[0]);
    },

    onCsvDrop (event) {
      const file = event.dataTransfer?.files?.[0];
      if (file) this.loadCsvFile(file);
    },

    loadCsvFile (file) {
      this.csvParseError = '';
      this.csvFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          this.csvRows = this.parseCsvText(e.target?.result);
        } catch (err) {
          this.csvParseError = err.message;
          this.csvRows = [];
        }
      };
      reader.readAsText(file);
    },

    async createAllFromCsv () {
      this.isCreatingBulk = true;
      this.csvProgress = 0;

      for (let i = 0; i < this.csvRows.length; i++) {
        const row = this.csvRows[i];
        row._status = 'pending';

        try {
          const hasAddress = !!(row.address && row.city && row.zip);

          let address;
          if (hasAddress) {
            const streetParts = row.address.split(' ');
            const streetNumber = streetParts[0];
            const street = streetParts.slice(1).join(' ');
            address = {
              streetNumber,
              street,
              city: row.city,
              state: row.state,
              zip: row.zip,
              county: '',
              fullAddress: `${row.address}, ${row.city}, ${row.state} ${row.zip}`
            };
          } else {
            address = this.generateAddress(row.productType, row.state);
          }

          const name = row.name || 'Admin Generated';

          const hasMailing = !!(row.mailingAddress && row.mailingCity && row.mailingState && row.mailingZip);

          await this.createPlSubmission({
            productType: row.productType,
            name,
            state: row.state,
            address,
            mailingAddress: hasMailing ? { street: row.mailingAddress, city: row.mailingCity, state: row.mailingState.toUpperCase(), zip: row.mailingZip } : null
          });

          row._status = 'success';
        } catch (err) {
          row._status = 'error';
          row._error = err.message;
        }

        this.csvProgress = i + 1;
      }

      this.isCreatingBulk = false;
      const successes = this.csvRows.filter(r => r._status === 'success').length;
      this.$store.dispatch('toastSuccess', { Message: `Done: ${successes}/${this.csvRows.length} submissions created.` });
    },

    clearCsv () {
      this.csvFile = null;
      this.csvRows = [];
      this.csvParseError = '';
      this.csvProgress = 0;
      this.$refs.csvFileInput.value = '';
    },

    getSubmissionUrl (submissionId) {
      // Links to the PL portal quote view
      return `/quote/quote?id=${submissionId}#quotes`;
    },

    resetForm () {
      this.form = {
        productType: '',
        state: '',
        address: {
          streetNumber: '',
          street: '',
          city: '',
          zip: ''
        },
        firstName: '',
        lastName: '',
        mailing: {
          enabled: false,
          street: '',
          city: '',
          state: '',
          zip: ''
        }
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/style.scss';

.generator-content-well {
  min-height: 400px;
  padding: 16px 0;
}

.mode-toggle {
  display: flex;
  align-items: center;
}

.results-section {
  border-top: 2px solid $gray5;
  padding-top: 32px;
}

.results-table {
  border: 1px solid $gray5;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  background-color: $gray5;
}

.row-alt {
  background-color: $gray6;
}

.csv-dropzone {
  position: relative;
  border: 2px dashed $gray5;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: $gray3;
  }
}

.csv-file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.csv-dropzone-label {
  pointer-events: none;
  font-size: 14px;
  color: $gray3;
}

.p-3 {
  padding: 12px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mb-6 {
  margin-bottom: 24px;
}

.mt-8 {
  margin-top: 32px;
}

.mt-4 {
  margin-top: 16px;
}

.mr-4 {
  margin-right: 16px;
}

.text-lg {
  font-size: 18px;
}

.font-semibold {
  font-weight: 600;
}

.w-full {
  width: 100%;
}

.text-left {
  text-align: left;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
